import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api';

const AuthContext = createContext(null);

const SAFE_PUBLIC_ROUTES = new Set(['/', '/feed', '/explore', '/login', '/signup']);
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const TOKEN_EXPIRES_KEY = 'auth_token_expires';
const REFRESH_EXPIRES_KEY = 'auth_refresh_expires';

const interpretLoginError = (error) => {
  const rawMessage = String(error?.message || '').trim();
  const normalized = rawMessage.toLowerCase();

  if (!rawMessage) {
    return { code: 'unknown', message: 'Login failed. Please try again.' };
  }

  if (
    normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('network error') ||
    normalized.includes('timeout')
  ) {
    return {
      code: 'network',
      message: 'Unable to reach the login service. Please check your connection and try again.'
    };
  }

  if (normalized.includes('cors')) {
    return {
      code: 'cors',
      message: 'Browser blocked the login request due to CORS. Please refresh or try again shortly.'
    };
  }

  if (normalized.includes('401') || normalized.includes('invalid credentials')) {
    return {
      code: 'invalid',
      message: 'Invalid email or password. Please try again.'
    };
  }

  if (normalized.includes('404')) {
    return {
      code: 'not-found',
      message: 'Login service is temporarily unavailable (404). Please retry in a moment.'
    };
  }

  if (normalized.includes('500') || normalized.includes('502') || normalized.includes('503')) {
    return {
      code: 'server',
      message: 'Server error while logging in. Please try again shortly.'
    };
  }

  return {
    code: 'unknown',
    message: rawMessage
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem('auth_remember') === 'true'
  );
  const [authNotice, setAuthNotice] = useState(null);

  const isOnSafeRoute = useCallback(() => {
    if (SAFE_PUBLIC_ROUTES.has(location.pathname)) {
      return true;
    }
    if (location.pathname.startsWith('/profile/')) {
      return true;
    }
    return false;
  }, [location.pathname]);

  const pushAuthNotice = useCallback((type, message) => {
    if (!message) return;
    setAuthNotice({
      id: Date.now(),
      type,
      message,
      timestamp: Date.now()
    });
  }, []);

  const clearAuthNotice = useCallback(() => setAuthNotice(null), []);

  const redirectToSafeEntry = useCallback((reason = 'logout', { redirectTo = '/feed', force = false } = {}) => {
    const currentlySafe = isOnSafeRoute();
    if (!force && currentlySafe) {
      return;
    }

    const params = new URLSearchParams();
    if (reason) {
      params.set('auth', reason);
    }

    const search = params.toString();
    const targetPath = redirectTo || '/feed';
    const destination = search ? `${targetPath}?${search}` : targetPath;

    const isAlreadyThere =
      location.pathname === targetPath && (location.search || '') === (search ? `?${search}` : '');

    if (!isAlreadyThere) {
      navigate(destination, { replace: true });
    }
  }, [isOnSafeRoute, location.pathname, location.search, navigate]);

  const clearAuthState = useCallback(() => {
    setToken(null);
    setUser(null);
    setRememberMe(false);
    localStorage.removeItem('auth_token');
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRES_KEY);
    localStorage.removeItem(REFRESH_EXPIRES_KEY);
    localStorage.removeItem('auth_session');
    localStorage.removeItem('auth_remember');
  }, [setToken, setUser, setRememberMe]);

  const forceLogout = useCallback((reason = 'logout', options = {}) => {
    clearAuthState();

    if (options.message) {
      pushAuthNotice(options.noticeType || (reason === 'expired' ? 'warning' : 'info'), options.message);
    }

    if (!options.skipRedirect) {
      redirectToSafeEntry(reason, {
        redirectTo: options.redirectTo,
        force: options.forceRedirect
      });
    }
  }, [clearAuthState, pushAuthNotice, redirectToSafeEntry]);

  const loadUser = useCallback(async () => {
    try {
      const response = await apiService.getCurrentUser();
      console.log('🔍 getCurrentUser response:', response);
      // API returns {user: {...}}, extract just the user object
      const userData = response.user || response;
      console.log('👤 Setting user to:', userData);
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      // If the backend says token is invalid/expired, clear it.
      const message = String(error?.message || '').toLowerCase();
      if (
        message.includes('401') ||
        message.includes('403') ||
        message.includes('access token') ||
        message.includes('expired')
      ) {
        forceLogout('expired', {
          message: 'Your session expired. Please log in again.',
          noticeType: 'warning'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [forceLogout]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Check for client-side session expiry when not in remember-me mode
    try {
      const stored = localStorage.getItem('auth_session');
      if (stored) {
        const { expiresAt } = JSON.parse(stored);
        if (expiresAt && Date.now() > expiresAt) {
          forceLogout('expired', {
            message: 'Your session expired. Please log in again.',
            noticeType: 'warning'
          });
          setLoading(false);
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to parse auth_session, clearing state:', e);
      forceLogout('expired', {
        message: 'Session data was invalid. Please log in again.',
        noticeType: 'warning',
        forceRedirect: true
      });
      setLoading(false);
      return;
    }

    loadUser();
  }, [token, forceLogout, loadUser]);

  const persistAuth = useCallback((nextToken, nextUser, remember, meta = {}) => {
    setToken(nextToken);
    setUser(nextUser);
    setRememberMe(remember);

    localStorage.setItem('auth_token', nextToken);
    localStorage.setItem('auth_remember', remember ? 'true' : 'false');

    if (Object.prototype.hasOwnProperty.call(meta, 'refreshToken')) {
      if (meta.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, meta.refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    }

    if (typeof meta.expiresIn === 'number' && Number.isFinite(meta.expiresIn)) {
      const expiresAt = Date.now() + meta.expiresIn * 1000;
      localStorage.setItem(TOKEN_EXPIRES_KEY, String(expiresAt));
    } else if (meta.expiresIn === null) {
      localStorage.removeItem(TOKEN_EXPIRES_KEY);
    }

    if (meta.refreshTokenExpiresAt) {
      localStorage.setItem(REFRESH_EXPIRES_KEY, meta.refreshTokenExpiresAt);
    }

    if (remember) {
      // Long-lived: rely on backend expiry + refresh tokens
      localStorage.removeItem('auth_session');
    } else {
      const ttlMinutes = meta.sessionTtlMinutes ?? 60;
      const expiresAt = Date.now() + ttlMinutes * 60 * 1000;
      localStorage.setItem('auth_session', JSON.stringify({ expiresAt }));
    }
  }, [setToken, setUser, setRememberMe]);

  useEffect(() => {
    const handlers = {
      getAccessToken: () => localStorage.getItem('auth_token'),
      getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
      onTokensUpdated: (payload = {}) => {
        const accessToken = payload.accessToken || payload.token;
        const nextUser = payload.user || user;
        persistAuth(accessToken, nextUser, rememberMe, {
          refreshToken: payload.refreshToken,
          expiresIn: payload.expiresIn,
          refreshTokenExpiresAt: payload.refreshTokenExpiresAt
        });
        clearAuthNotice();
      },
      onUnauthorized: (error) => {
        console.warn('Auth refresh failed, forcing logout:', error);
        pushAuthNotice('warning', 'Your session expired. Please log in again.');
        forceLogout('expired', {
          message: 'Your session expired. Please log in again.',
          noticeType: 'warning',
          forceRedirect: true
        });
      }
    };

    apiService.registerAuthHandlers(handlers);

    return () => {
      apiService.registerAuthHandlers({
        onTokensUpdated: null,
        onUnauthorized: null
      });
    };
  }, [rememberMe, user, persistAuth, forceLogout, pushAuthNotice, clearAuthNotice]);

  const login = async (email, password, remember = false) => {
    try {
      const response = await apiService.login(email, password, remember);
      const accessToken = response.accessToken || response.token;
      persistAuth(accessToken, response.user, remember, {
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
        refreshTokenExpiresAt: response.refreshTokenExpiresAt
      });
      clearAuthNotice();
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      const interpreted = interpretLoginError(error);
      const friendlyError = new Error(interpreted.message);
      friendlyError.code = interpreted.code;
      friendlyError.rawMessage = error?.message || null;
      friendlyError.cause = error;
      throw friendlyError;
    }
  };

  const signup = async (username, email, password, remember = false) => {
    const response = await apiService.signup(username, email, password, remember);
    const accessToken = response.accessToken || response.token;
    persistAuth(accessToken, response.user, remember, {
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
      refreshTokenExpiresAt: response.refreshTokenExpiresAt
    });
    clearAuthNotice();
    return response;
  };

  const logout = async (options = {}) => {
    try {
      const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
      await apiService.logout(storedRefresh);
    } catch (error) {
      console.warn('Logout request failed:', error?.message || error);
    } finally {
      forceLogout('logout', {
        message: 'You have been logged out.',
        noticeType: 'info',
        redirectTo: options.redirectTo || '/feed',
        forceRedirect: true
      });
    }
  };

  const updateProfile = async (updates) => {
    const updated = await apiService.updateProfile(updates);
    setUser(updated);
    return updated;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        signup,
        logout,
        updateProfile,
        token,
        rememberMe,
        setRememberMe,
        authNotice,
        clearAuthNotice
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
